import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useDictionary } from "hooks/use-dictionary";
import { useState } from "react";
import { PublicProgramFilters } from "types/program";
import { Checkbox, FormControlLabel, MenuItem } from "@mui/material";
import { useUrlParams } from "hooks/use-url-params";
import FieldSelect from "components/field-select";
import { ClassifierType } from "types/classifier";
import { usePublicClassifierValues } from "hooks/api/system-admin/classifiers/use-public-classifier-values";
import { usePublicOrganizerInstitutionsForPrograms } from "hooks/api/use-public-organizer-institutions-for-programs";

interface FiltersBarProps {
  onSubmit: (val: PublicProgramFilters) => void;
  onClear: () => void;
}

const FiltersBar = ({ onSubmit, onClear }: FiltersBarProps) => {
  const { filters: urlFilters, setUrlFilters } = useUrlParams();
  const defaultFilters: PublicProgramFilters = {
    name: null,
    organizerId: null,
    identification: null,
    rating: null,
    otherTargetedAudienceGroup: null,
    targetedAudienceClassifierId: null,
    includeExpired: null,
  };
  const [filters, setFilters] = useState<PublicProgramFilters>({
    ...defaultFilters,
    ...urlFilters,
  });
  const includeExpired = Boolean(filters.includeExpired);
  const { items: institutions } = usePublicOrganizerInstitutionsForPrograms(includeExpired);
  const {
    items: otherTargetedAudienceClassifiers,
    loadMore: otherTargetedAudienceClassifiersLoadMore,
    setFilters: setOtherTargetedAudienceClassifierFilters
  } = usePublicClassifierValues(ClassifierType.OTHER_TARGETED_AUDIENCE);
  const { items: targetedAudienceClassifiers, loadMore: targetedAudienceClassifiersLoadMore, setFilters: setTargetedAudienceClassifierFilters } = usePublicClassifierValues(ClassifierType.TARGETED_AUDIENCE);
  const dict = useDictionary();

  return (
    <div className="rounded bg-white border p-5 grid sm:grid-cols-2 gap-5">
      <TextField
        label={dict.programs.programName}
        value={filters.name ?? ""}
        onChange={(e) => {
          setFilters((prev) => ({ ...prev, name: e.target.value || null }));
        }}
      />
      <TextField
        label={dict.programs.programOrganizer}
        value={filters.organizerId ?? ""}
        select
        onChange={(e) => {
          setFilters((prev) => ({ ...prev, organizerId: e.target.value ? Number(e.target.value) : null }));
        }}
      >
        {institutions.map((institution, i) => (
          <MenuItem key={i} value={institution.id}>
            {institution.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={dict.programs.identificationNumber}
        value={filters.identification ?? ""}
        onChange={(e) => {
          setFilters((prev) => ({ ...prev, identification: e.target.value || null }));
        }}
      />
      <TextField
        label={dict.organizerAdmin.events.minimalReview}
        type="number"
        value={filters.rating ?? ""}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        onKeyDown={(e) => {
          if (["e", "-", "+"].includes(e.key.toLowerCase())) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let val = parseFloat(e.target.value);
          if (val < 1) val = 1;
          if (val > 5) val = 5;
          setFilters((prev) => ({ ...prev, rating: val }));
        }}
        inputProps={{ min: 1, max: 5, step: 0.1 }}
      />
      <FieldSelect
        label={dict.organizerAdmin.events.targetedAudienceClassifier}
        options={targetedAudienceClassifiers.map((targetedAudienceClassifier) => ({ value: targetedAudienceClassifier.id, label: targetedAudienceClassifier.valueName }))}
        onChange={(val) => setFilters((prev) => ({ ...prev, targetedAudienceClassifierId: val }))}
        setFilters={setTargetedAudienceClassifierFilters} 
        lazyLoadable
        onBottomReached={targetedAudienceClassifiersLoadMore}
        value={filters.targetedAudienceClassifierId ?? ''}
      />
      <FieldSelect
        label={dict.organizerAdmin.events.otherTargetedAudienceClassifier}
        options={otherTargetedAudienceClassifiers.map((classifier) => ({ 
          value: classifier.id, 
          label: classifier.valueName 
        }))}
        onChange={(val) => setFilters((prev) => ({ 
          ...prev, 
          otherTargetedAudienceGroup: val 
        }))}
        setFilters={setOtherTargetedAudienceClassifierFilters} 
        lazyLoadable
        onBottomReached={otherTargetedAudienceClassifiersLoadMore}
        value={filters.otherTargetedAudienceGroup ?? ''}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={includeExpired}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                includeExpired: e.target.checked ? true : null,
              }));
            }}
          />
        }
        label={dict.common.includeExpiredPrograms}
      />
      <div className="flex gap-3">
        <Button
          onClick={() => {
            onSubmit(filters);
            setUrlFilters(filters);
          }}
          color="primary"
          variant="contained"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
          type="submit"
        >
          {dict.common.search}
        </Button>
        <Button
          onClick={() => {
            onClear();
            setFilters(defaultFilters);
            setUrlFilters(defaultFilters);
          }}
          color="primary"
          variant="outlined"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
        >
          {dict.common.clear}
        </Button>
      </div>
    </div>
  );
};

export default FiltersBar;
